import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"
import { RootState } from "./store"
import type { UiLanguage } from "../lib/i18n/language"

export type PlannerCompany = {
  id: number
  tin: string
  name: string
}

export type Settings = {
  mobileNotificationsEnabled: boolean
  notifyOnlyOnDriverMessages: boolean
  pushSubscriptionPresent: boolean
  showNicknameInWhatsapp: boolean
  showTranslations: boolean
  plannerCompanies: PlannerCompany[]
  // Optional on purpose: unset means "not chosen" (legacy UI), and old
  // persisted state rehydrates without it.
  uiLanguage?: UiLanguage
}

const initialState: Settings = {
  mobileNotificationsEnabled: false,
  notifyOnlyOnDriverMessages: false,
  pushSubscriptionPresent: false,
  showNicknameInWhatsapp: false,
  showTranslations: true,
  plannerCompanies: [],
}

type UpdateSettingsAction = {
  settings: Partial<Settings>
}

export const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    updateSettings: (state, action: PayloadAction<UpdateSettingsAction>) => {
      return {
        ...state,
        ...action.payload.settings,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const selectSettings = (state: RootState) => state.settings

// `settings` is a persisted slice and the store migrator only resets on a
// STATE_VERSION change, so an already logged-in user rehydrates a settings
// object with no plannerCompanies key at all. An empty selection means "show
// everything", so `?? []` is both the safe and the correct degradation.
export const selectPlannerCompanies = (state: RootState): PlannerCompany[] =>
  state.settings.plannerCompanies ?? []

export const selectUiLanguage = (state: RootState): UiLanguage | undefined =>
  state.settings.uiLanguage

export const { updateSettings } = settingsSlice.actions
export default settingsSlice.reducer
